import { useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { updateAttendanceLog, createAttendanceLog, getMemberAttendanceLogs } from "../../services/attendanceService";
import { getAllMembersWithDetails } from "../../services/memberService";

// "Edit entry" modal — edits a single attendance record (check-in / check-out
// times, work type, status, note). Shared across dashboard, history and profile.
// A log without an `id` opens it in "Add entry" mode, which creates the record instead
// (e.g. for a day the member forgot to sign in).
//
// Props:
//   log       attendance-log row (id, memberName, memberTitle, memberImgUrl,
//             checkIn "HH:mm:ss", checkout, workType, status, note, workDate, ...)
//             Add mode needs memberId (slackUserId is looked up if not passed);
//             workDate may be empty to let the admin pick the day.
//   onClose() close without saving
//   onSaved(updatedLog)  called after a successful save with the merged row
//   onCreated(newLog)    add mode: called after the record was created
//   onDelete(log)        called when "Delete entry" is pressed (host runs the
//                        confirm + delete flow so it stays consistent everywhere)

const STATUS_OPTIONS = [
  { value: "PRESENT", label: "Present", cls: "present" },
  { value: "LATE", label: "Late", cls: "late" },
  { value: "ON_BREAK", label: "On break", cls: "break" },
  { value: "ABSENT", label: "Absent", cls: "absent" },
];
// A back-filled day is either on time or late. Absence is automatic (no record that day),
// and "On break" can't apply to a past day.
const ADD_STATUS_OPTIONS = STATUS_OPTIONS.filter((o) => o.value === "PRESENT" || o.value === "LATE");

const toTimeInput = (t) => (t ? String(t).slice(0, 5) : "");

const avatarFor = (log) =>
  log?.memberImgUrl ? (
    <img className="av av-36" src={log.memberImgUrl} alt="" style={{ objectFit: "cover" }} />
  ) : (
    <div className="av av-36 av-MH">{(log?.memberName || "?").slice(0, 2).toUpperCase()}</div>
  );

export default function EditEntryModal({ log, onClose, onSaved, onCreated, onDelete }) {
  const [checkIn, setCheckIn] = useState(toTimeInput(log?.checkIn));
  const [checkout, setCheckout] = useState(toTimeInput(log?.checkout));
  const [workType, setWorkType] = useState(log?.workType === "REMOTE" ? "REMOTE" : "OFFICE");
  const [status, setStatus] = useState(log?.status || "");
  const [note, setNote] = useState(log?.note || "");
  const [workDate, setWorkDate] = useState(log?.workDate || "");
  const [saving, setSaving] = useState(false);

  if (!log) return null;

  const isNew = !log.id;
  // The day is fixed when adding from an absent row; the Quick action lets the admin pick it.
  const dateEditable = isNew && !log.workDate;
  const today = moment().format("YYYY-MM-DD");
  const dateLabel = workDate ? moment(workDate).format("ddd, MMM D, YYYY") : "";
  const statusOptions = isNew ? ADD_STATUS_OPTIONS : STATUS_OPTIONS;

  const handleCreate = async () => {
    if (!workDate) return toast.error("Pick the date for this entry.");
    if (workDate > today) return toast.error("You can't add an entry for a future date.");
    if (!checkIn || !checkout) return toast.error("Add both the check-in and check-out time.");

    setSaving(true);
    // A record is keyed by both the member id and their Slack user id. Hosts pass the Slack id
    // when they have it; otherwise look it up from the member list.
    let slackUserId = log.slackUserId;
    if (!slackUserId && log.memberId) {
      const details = await getAllMembersWithDetails();
      slackUserId = details?.[log.memberId]?.slackUserId;
    }
    if (!log.memberId || !slackUserId) {
      setSaving(false);
      return toast.error("Couldn't identify this member. Reload the page and try again.");
    }
    // The member may have signed in that day after all (or someone added it already) —
    // a second session would double the day, so point the admin to the existing row.
    const existing = await getMemberAttendanceLogs(log.memberId, { startDate: workDate, endDate: workDate });
    if (existing === null) {
      setSaving(false);
      return toast.error("Couldn't check existing entries. Try again.");
    }
    if (existing.length) {
      setSaving(false);
      return toast.error(`${moment(workDate).format("MMM D")} already has an entry. Edit that row instead.`);
    }

    const { status: createStatus, log: created } = await createAttendanceLog({
      memberId: log.memberId,
      slackUserId,
      workDate,
      checkInClockTime: checkIn,
      checkOutClockTime: checkout,
    });
    if (createStatus !== 200 || !created?.id) {
      setSaving(false);
      return toast.error("Failed to add entry");
    }

    // Create only stores the times. The update call saves work type / status / note and
    // recomputes the session's net hours, same as editing a normal entry.
    const { status: updateStatus } = await updateAttendanceLog(created.id, {
      checkInClockTime: checkIn,
      checkOutClockTime: checkout,
      workType,
      status,
      note,
    });
    setSaving(false);
    if (updateStatus === 200) toast.success("Entry added");
    else toast.error("Entry added, but work type, status and note weren't saved. Edit the entry to set them.");
    onCreated?.(created);
    onClose?.();
  };

  const handleSave = async () => {
    if (isNew) return handleCreate();
    if (!checkIn) {
      toast.error("Check-in time can't be empty!");
      return;
    }
    setSaving(true);
    const { status: httpStatus, log: updated } = await updateAttendanceLog(log.id, {
      checkInClockTime: checkIn,
      checkOutClockTime: checkout,
      workType,
      status,
      note,
    });
    setSaving(false);
    if (httpStatus === 200) {
      onSaved?.({
        ...log,
        ...updated,
        checkIn: updated?.checkIn || checkIn,
        checkout: updated?.checkout ?? (checkout || null),
        workType,
        status: status || null,
        note: note || null,
      });
      toast.success("Entry updated");
      onClose?.();
    } else {
      toast.error("Failed to update entry");
    }
  };

  return (
    <div className="modal-overlay blur" onClick={onClose}>
      <div className="modal-card modal-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="edit-modal-head">
          {avatarFor(log)}
          <div className="em-info">
            <b>{log.memberDisplayName || log.memberName}</b>
            <span>{[isNew ? "Add entry" : log.memberTitle, dateLabel].filter(Boolean).join(" · ")}</span>
          </div>
          <button type="button" className="em-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="edit-modal-body">
          {dateEditable && (
            <div className="em-field">
              <label htmlFor="em-date">Date</label>
              <input id="em-date" type="date" className="em-input" max={today} value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
            </div>
          )}
          <div className="em-grid2">
            <div className="em-field">
              <label htmlFor="em-checkin">Check-in</label>
              <input id="em-checkin" type="time" className="em-input" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
            </div>
            <div className="em-field">
              <label htmlFor="em-checkout">Check-out</label>
              <input id="em-checkout" type="time" className="em-input" value={checkout} onChange={(e) => setCheckout(e.target.value)} />
            </div>
          </div>

          <div className="em-field">
            <label>Work type</label>
            <div className="em-toggles">
              <button type="button" className={`em-toggle ${workType === "OFFICE" ? "on" : ""}`} onClick={() => setWorkType("OFFICE")}>
                <svg><use href="#i-building" /></svg>Office
              </button>
              <button type="button" className={`em-toggle ${workType === "REMOTE" ? "on" : ""}`} onClick={() => setWorkType("REMOTE")}>
                <svg><use href="#i-wifi" /></svg>Remote
              </button>
            </div>
          </div>

          <div className="em-field">
            <label>Status</label>
            <div className="em-toggles">
              {statusOptions.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  className={`em-toggle ${opt.cls} ${status === opt.value ? "on" : ""}`}
                  onClick={() => setStatus((s) => (s === opt.value ? "" : opt.value))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="em-field">
            <label htmlFor="em-note">Note <span style={{ color: "var(--muted-2)", fontWeight: 500 }}>(optional)</span></label>
            <textarea id="em-note" className="em-input" placeholder="Add a note about this entry…" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {/* Footer */}
        <div className="edit-modal-foot">
          {onDelete && !isNew && (
            <button type="button" className="del-link" onClick={() => onDelete(log)}>
              <svg><use href="#i-trash" /></svg>Delete entry
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-brand" disabled={saving} onClick={handleSave}>
            {saving ? "Saving…" : isNew ? "Add entry" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
